import { Box } from '@mui/material';
import { CurrencyAmount, Token } from '@uniswap/sdk-core';
import {
  Chain,
  Ellipsis,
  getCurrencyAmount,
  getTransactionUrl,
  LocalStorageContext,
  MultiPageActionModalV3,
  ToastContext,
  WalletConnectionContext,
  Web3Context,
} from 'common-client';
import { ContractReceipt, Signer } from 'ethers';
import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { AuctionCreatedModal } from '../../components/AuctionCreatedModal';
import { CreateAuctionRebaseWarningModal } from '../../components/CreateAuctionRebaseWarningModal';
import { SelectEndDateBox } from '../../components/SelectEndDateBox';
import { SelectPriceRangeBox } from '../../components/SelectPriceRangeBox';
import { ConfigContext } from '../../config';
import { createAuction as createAuctionTx } from '../../transactions';
import { AuctionCreatedEvent } from '../../types/IAuctionFactory';
import { SelectPairBoxWrapper } from './SelectPairBoxWrapper';

export interface CreateAuctionModalWrapperProps {
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * Callback on exiting
   */
  onExit?: () => void;
}

interface WarningState {
  /**
   * Whether the user has already dismissed the warning
   */
  dismissed: boolean;
}

const NUMBER_OF_TICKS = 100;

function createAuction(
  signer: Signer | undefined,
  auctionFactoryAddress: string | undefined,
  bidAsset: Token | null,
  askAsset: Token | null,
  minPrice: CurrencyAmount<Token> | null,
  maxPrice: CurrencyAmount<Token> | null,
  tickWidth: CurrencyAmount<Token> | null,
  endDate: Date | null,
): Promise<ContractReceipt> {
  if (!signer) {
    return Promise.reject(Error('Signer is undefined'));
  }
  if (!auctionFactoryAddress) {
    return Promise.reject(Error('Auction Factory address is undefined'));
  }
  if (!bidAsset) {
    return Promise.reject(Error('Bid asset is null'));
  }
  if (!askAsset) {
    return Promise.reject(Error('Ask asset is null'));
  }
  if (!minPrice) {
    return Promise.reject(Error('Minimum price is null'));
  }
  if (!maxPrice) {
    return Promise.reject(Error('Maximum price is null'));
  }
  if (!tickWidth) {
    return Promise.reject(Error('Tick width is null'));
  }
  if (!endDate) {
    return Promise.reject(Error('endDate is null'));
  }

  const endDateSeconds = Math.floor(endDate.getTime() / 1000);
  return createAuctionTx(
    signer,
    auctionFactoryAddress,
    bidAsset,
    askAsset,
    minPrice,
    maxPrice,
    tickWidth,
    endDateSeconds,
  );
}

export function CreateAuctionModalWrapper({
  open,
  onExit,
}: CreateAuctionModalWrapperProps) {
  const { selectWallet, signer, chainId } = useContext(Web3Context);
  const {
    connected,
    changeNetworkHandler,
    connectedToSupportedNetwork,
  } = useContext(WalletConnectionContext);
  const { enqueueTransactionResult } = useContext(ToastContext);
  const { networkConfig } = useContext(ConfigContext);
  const { read, write } = useContext(LocalStorageContext);
  const history = useHistory();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [sellAsset, setSellAsset] = useState<Token | null>(null);
  const [bidAsset, setBidAsset] = useState<Token | null>(null);
  const [minimumPrice, setMinimumPrice] = useState<CurrencyAmount<Token> | null>(null);
  const [tickWidth, setTickWidth] = useState<CurrencyAmount<Token> | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [transactionUrl, setTransactionUrl] = useState<string | undefined>(undefined);
  const [newAuctionAddress, setNewAuctionAddress] = useState<string | null>(null);
  const [escapeHatchMessage, setEscapeHatchMessage] = useState<string | null>(null);
  const [warningState, setWarningState] = useState<WarningState>(
    read<WarningState>('auction-client', 'create-auction-rebase-warning') || { dismissed: false },
  );

  const connectCallback = useCallback(() => {
    if (connected) {
      changeNetworkHandler(Chain.Mainnet);
    } else {
      setLoading(true);
      selectWallet().finally(() => setLoading(false));
    }
  }, [connected, selectWallet, changeNetworkHandler]);

  const createAuctionCallback = useCallback(() => {
    setLoading(true);
    // Ensure user doesn't accidentally exit during pending txs
    setEscapeHatchMessage('Are you sure you want to close this modal? You have pending transactions.');
    let maximumPrice: CurrencyAmount<Token> | null = null;
    if (minimumPrice && tickWidth) {
      maximumPrice = minimumPrice.add(tickWidth.multiply(100 - 1));
    }
    createAuction(
      signer,
      networkConfig?.auctionFactory,
      bidAsset,
      sellAsset,
      minimumPrice,
      maximumPrice,
      tickWidth,
      endDate,
    )
      .then((txReceipt) => {
        const txUrl = getTransactionUrl(
          chainId,
          txReceipt.transactionHash,
        );
        enqueueTransactionResult(
          'success',
          `Successfully created ${sellAsset?.symbol}-${bidAsset?.symbol} auction!`,
          txUrl,
        );
        if (txReceipt.events) {
          const matchingEvents = txReceipt.events.filter((event) => event.event === 'AuctionCreated');
          if (matchingEvents[0]) {
            const event: AuctionCreatedEvent = matchingEvents[0] as AuctionCreatedEvent;
            setNewAuctionAddress(event.args.newAuctionAddress);
          }
        }
        setTransactionUrl(txUrl);
        setActiveIndex(4);
      })
      .catch((error: Error) => {
        enqueueTransactionResult(
          'error',
          `Could not create auction: ${error.message}`,
        );
        setTransactionUrl(undefined);
      })
      .finally(() => {
        setLoading(false);
        // Ensure user doesn't accidentally skip success modal
        setEscapeHatchMessage('Are you sure you want to close this modal?');
      });
  }, [signer, sellAsset, bidAsset, minimumPrice, tickWidth, endDate]);

  useEffect(() => {
    if (bidAsset) {
      setMinimumPrice(getCurrencyAmount(0, bidAsset));
      setTickWidth(getCurrencyAmount(0, bidAsset));
    }
  }, [bidAsset]);

  const maximumPrice = useMemo(() => {
    if (minimumPrice && tickWidth) {
      return minimumPrice.add(tickWidth.multiply(NUMBER_OF_TICKS - 1));
    }
    return null;
  }, [minimumPrice, tickWidth]);

  if (!warningState.dismissed) {
    return (
      <CreateAuctionRebaseWarningModal
        open
        onExit={() => {
          const warningStateNew = {
            ...warningState,
            dismissed: true,
          };
          setWarningState(warningStateNew);
        }}
        onAccept={() => {
          const warningStateNew = {
            ...warningState,
            dismissed: true,
          };
          write('auction-client', 'create-auction-rebase-warning', warningStateNew);
          setWarningState(warningStateNew);
        }}
      />
    );
  }

  return (activeIndex < 3)
    ? (
      <MultiPageActionModalV3
        open={open}
        title="Create Auction"
        activeIndex={activeIndex}
        onExit={onExit}
        escapeHatchMessage={escapeHatchMessage}
      >
        <SelectPairBoxWrapper
          sellAsset={sellAsset || undefined}
          onSellAssetChange={setSellAsset}
          bidAsset={bidAsset || undefined}
          onBidAssetChange={setBidAsset}
          onNextClick={() => setActiveIndex((prevActiveIndex) => prevActiveIndex + 1)}
        />
        <SelectPriceRangeBox
          sellAsset={sellAsset}
          minimumPrice={minimumPrice}
          onMinimumPriceUpdate={setMinimumPrice}
          tickWidth={tickWidth}
          onTickWidthUpdate={setTickWidth}
          maximumPrice={maximumPrice}
          onBackClick={() => setActiveIndex((prevActiveIndex) => prevActiveIndex - 1)}
          onNextClick={() => setActiveIndex((prevActiveIndex) => prevActiveIndex + 1)}
        />
        <Box position="relative">
          <SelectEndDateBox
            date={endDate}
            isUserConnected={connectedToSupportedNetwork}
            onUpdateDate={setEndDate}
            onBackClick={() => setActiveIndex((prevActiveIndex) => prevActiveIndex - 1)}
            onNextClick={connectedToSupportedNetwork ? createAuctionCallback : connectCallback}
          />
          {
            loading && (
              <Box
                position="absolute"
                top={0}
                bottom={0}
                left={0}
                right={0}
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
              >
                <Ellipsis />
              </Box>
            )
          }
        </Box>
      </MultiPageActionModalV3>
    )
    : (
      <AuctionCreatedModal
        leftToken={sellAsset}
        rightToken={bidAsset}
        onGoToAuction={newAuctionAddress ? () => {
          history.push(`/auction/${newAuctionAddress}`);
        } : undefined}
        onClose={onExit}
        transactionLink={transactionUrl}
        transactionText={undefined}
        open={open}
        escapeHatchMessage={escapeHatchMessage}
      />
    );
}
